//auth 2.0
import * as Dom from "./utils/dom";
import Caret from "./utils/caret";
import "./index.css";
import { alphabetize, cloneDeep, convertToArrow, getCaretPos, getTextDataToMerge, makeElement, romanize, setCursorAtPosition } from "../../../../utils/utils";

/**
 * List Tool for the Editor.js 2.0
 */
class OrderedList {
  /**
   * Notify core that read-only mode is supported
   *
   * @returns {boolean}
   */
  static get isReadOnlySupported() {
    return true;
  }

  /**
   * Allow to use native Enter behavior
   *
   * @returns {boolean}
   * @publicBN
   */
  static get enableLineBreaks() {
    return true;
  }

  /**
   * Get Tool toolbox settings
   * icon - Tool icon's SVG
   * title - title to show in toolbox
   *
   * @returns {{icon: string, title: string}}
   */
  static get toolbox() {
    return {
      icon: `<svg width="15" height="14" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.74245 0.062679C1.90143 0.150792 2.00007 0.318238 2.00007 0.5V5.5C2.00007 5.77614 1.77621 6 1.50007 6C1.22392 6 1.00007 5.77614 1.00007 5.5V1.40212L0.765066 1.549C0.530898 1.69535 0.222423 1.62417 0.0760678 1.39C-0.0702874 1.15583 0.000899198 0.847356 0.235067 0.701001L1.23507 0.076001C1.3892 -0.0203329 1.58347 -0.0254338 1.74245 0.062679ZM4.5 1.5C4.5 1.22386 4.72386 1 5 1H14.5C14.7761 1 15 1.22386 15 1.5C15 1.77614 14.7761 2 14.5 2H5C4.72386 2 4.5 1.77614 4.5 1.5ZM4.5 7C4.5 6.72386 4.72386 6.5 5 6.5H14.5C14.7761 6.5 15 6.72386 15 7C15 7.27614 14.7761 7.5 14.5 7.5H5C4.72386 7.5 4.5 7.27614 4.5 7ZM5 12C4.72386 12 4.5 12.2239 4.5 12.5C4.5 12.7761 4.72386 13 5 13H14.5C14.7761 13 15 12.7761 15 12.5C15 12.2239 14.7761 12 14.5 12H5ZM1.35604 9.02953C1.42178 9.00579 1.49179 8.9962 1.56149 9.00136C1.63119 9.00652 1.69902 9.02632 1.76056 9.05948C1.8221 9.09264 1.87595 9.1384 1.91861 9.19379C1.96126 9.24917 1.99176 9.31294 2.00811 9.38092C2.02388 9.44647 2.02614 9.51453 2.0148 9.58094C2.01397 9.58393 2.01228 9.58991 2.00939 9.59919C2.00337 9.61846 1.99479 9.64356 1.98437 9.6706C1.97388 9.69782 1.96284 9.72363 1.95229 9.74541C1.9471 9.75613 1.9426 9.76467 1.93898 9.77108C1.93556 9.77716 1.93374 9.77987 1.93374 9.77988L1.93378 9.77982C1.9232 9.79514 1.91348 9.81103 1.90467 9.82744L0.059498 13.2634C-0.0237152 13.4184 -0.0193906 13.6057 0.0708864 13.7566C0.161163 13.9076 0.324114 14 0.5 14H2.49421C2.77035 14 2.99421 13.7761 2.99421 13.5C2.99421 13.2239 2.77035 13 2.49421 13H1.33604L2.77479 10.3208C2.83921 10.2207 2.88712 10.109 2.91752 10.0301C2.94992 9.94598 2.9828 9.84533 2.99656 9.7715C3.03512 9.56454 3.02962 9.35178 2.98039 9.14709C2.93117 8.94241 2.83933 8.7504 2.71088 8.58361C2.58242 8.41683 2.42024 8.279 2.23491 8.17914C2.04958 8.07928 1.84528 8.01963 1.63533 8.00409C1.42538 7.98854 1.21452 8.01745 1.0165 8.08893C0.818487 8.16041 0.637775 8.27285 0.486163 8.41891C0.33455 8.56496 0.215443 8.74135 0.136616 8.93656C0.0332176 9.19261 0.156969 9.48401 0.413022 9.5874C0.669076 9.6908 0.960469 9.56705 1.06387 9.311C1.09005 9.24617 1.1296 9.18759 1.17995 9.13909C1.23029 9.09059 1.2903 9.05326 1.35604 9.02953Z" fill="currentColor" /></svg>`,
      title: "Numbered List",
    };
  }

  /**
   * Allow List Tool to be converted to/from other block
   *
   * @returns {{export: Function, import: Function}}
   */
  static get conversionConfig() {
    return {
      /**
       * To create exported string from list, concatenate items by dot-symbol.
       *
       * @param {ListData} data - list data to create a string from thats
       * @returns {string}
       */
      export: (data) => {
        return data.items.map(({ text }) => text).join(". ");
      },
      /**
       * To create a list from other block's string, just put it at the first item
       *
       * @param {string} string - string to create list tool data from that
       * @returns {ListData}
       */
      import: (string) => {
        return {
          items: [{ text: string }],
          indentLevel: 0, // 0 ~ 8
          indentPhase: 0,
          numberingLevels: [
            [1, 1, 1],
            [1, 1, 1],
            [1, 1, 1],
          ],
          numberForMarkdownDownloading: 1,
        };
      },
    };
  }

  /**
   * Render plugin`s main Element and fill it with saved data
   *
   * @param {object} params - tool constructor options
   * @param {ListData} params.data - previously saved data
   * @param {object} params.config - user config for Tool
   * @param {object} params.api - Editor.js API
   * @param {boolean} params.readOnly - read-only mode flag
   */
  constructor({ data, config, api, readOnly }) {
    /**
     * HTML nodes
     *
     * @private
     */
    this._elements = {
      container: null,
      input: null,
      pseudoBefore: null,
    };

    this.config = config;
    this.api = api;
    this.readOnly = readOnly;
    /**
     * Tool's data
     *
     * @type {ListData}
     */
    this.data =
      data && data.items
        ? data
        : {
            items: [{ text: "" }],
            indentLevel: 0, // 0 ~ 8
            indentPhase: 0,
            numberingLevels: [
              [1, 1, 1],
              [1, 1, 1],
              [1, 1, 1],
            ],
            numberForMarkdownDownloading: 1,
          };
    /** NOTE
     * * There is three numbering rules. Decimal -> Lower alphabet -> Lower roman numeral
     * * ex ) 1 -> a -> i
     * * But alphabet and roman is also from real number, not from own char. (a -> 1, b-> 2, aa -> 27)
     * * So, data.numberingLevels's inner array that length is 3 means [Decimal, alphabet, roman] 's each own numbering levels.
     * * If [3, 4, 2], it indicates [3(Decimal), d (alphabet), ii (roman)]
     * * Since editor supports 0 to 9 indents in slid-web@1.9.2,
     * * It will have a system  Decimal -> alphabet -> roman -> ... -> alphabet -> roman.
     * * So, Array [Decimal,alphabet,roman] is a one 'phase', which means one numbering cycle each three indents.
     * * Cause max indent is 9, there will be three phases!
     * * That's why data.numberingLevels have three Array[Decimal, Alphabet, Roman]
     */

    this.MAX_INDENT_LEVEL = 8;
    this.INDENT_MARGIN = 30;

    // * These three constants indicates [Decimal, Alphabet, Roman]
    this.DECIMAL_INDEX = 0;
    this.ALPHABET_NUMBER_INDEX = 1;
    this.ROMAN_NUMBER_INDEX = 2;
    this.setNumberingRules();
  }

  setNumberingRules() {
    this.numbering = [
      this.data.numberingLevels[this.data.indentPhase][this.DECIMAL_INDEX],
      alphabetize(this.data.numberingLevels[this.data.indentPhase][this.ALPHABET_NUMBER_INDEX]),
      romanize(this.data.numberingLevels[this.data.indentPhase][this.ROMAN_NUMBER_INDEX]),
    ];
  }

  setNumberForMarkdownDownloading() {
    this.data.numberForMarkdownDownloading = this.data.numberingLevels[this.data.indentPhase][this.data.indentLevel % 3];
  }

  onKeyDown(event) {
    convertToArrow(event);

    const [ENTER, BACKSPACE, TAB] = [13, 8, 9]; // key codes
    this.setNumberingRules();
    this.setNumberForMarkdownDownloading();
    switch (event.keyCode) {
      case ENTER:
        if (event.shiftKey) return;
        this.addNewOrderedList(event);
        break;
      case BACKSPACE:
        this.backspace(event);
        break;
      case TAB:
        if (event.shiftKey) {
          this.shiftTab(event);
        } else {
          this.addTab(event);
        }
        break;
      default:
        return;
    }
  }

  merge(data) {
    const caretPos = getCaretPos(this._elements.input);
    const newData = {
      ...this.data,
      items: [{ text: this.data.items[0].text + getTextDataToMerge(data) }],
      indentLevel: this.data.indentLevel, // 0 ~ 8
    };

    this.data = newData;
    this._elements.input.innerHTML = this.data.items[0].text;

    setTimeout(() => {
      setCursorAtPosition(this._elements.input, caretPos);
    });
  }

  /**
   * Styles
   *
   * @private
   */
  get CSS() {
    return {
      baseBlock: this.api.styles.block,
      container: "cdx-ordered-list-outer-wrapper",
      innerWrapper: "cdx-ordered-list-inner-wrapper",
      settingsWrapper: "cdx-ordered-list-settings",
      pseudoSelection: "cdx-ordered-list__pseudo-selection",
      pseudoBefore: "cdx-ordered-list__pseudo-before",
      inputWrapper: "cdx-ordered-list___input-wrapper",
      input: "cdx-ordered-list__input",
      settingsButton: this.api.styles.settingsButton,
      settingsButtonActive: this.api.styles.settingsButtonActive,
    };
  }

  /**
   * Returns list tag with item
   *
   * @returns {Element}
   * @public
   */
  render() {
    const makeElements = () => {
      this._elements.container = makeElement("div", [this.CSS.container]);
      this._elements.pseudoBefore = makeElement("div", [this.CSS.pseudoBefore]);
      const innerWrapper = makeElement("div", [this.CSS.innerWrapper]);
      const pseudoSelection = makeElement("div", [this.CSS.pseudoSelection]);
      const inputWrapper = makeElement("div", [this.CSS.inputWrapper]);
      this._elements.input = makeElement("div", [this.CSS.input], {
        contentEditable: !this.readOnly,
        innerHTML: this.data.items[0].text || "",
      });
      pseudoSelection.appendChild(this._elements.pseudoBefore);
      inputWrapper.appendChild(this._elements.input);
      innerWrapper.appendChild(pseudoSelection);
      innerWrapper.appendChild(inputWrapper);
      this._elements.container.appendChild(innerWrapper);
      this._elements.container.style.marginLeft = `${this.INDENT_MARGIN * this.data.indentLevel}px`;
    };

    const setCypressAttribute = () => {
      this._elements.container.setAttribute("slid-cy", "editor-ordered-list");
      this._elements.pseudoBefore.setAttribute("slid-cy", "editor-ordered-list-pseudoBefore");
      this._elements.input.setAttribute("slid-cy", "editor-ordered-list-input");
    };

    const setMutationObserverForInput = () => {
      this._elements.input.addEventListener("input", (e) => {
        this.data.items[0].text = this._elements.input.innerHTML;
      });
      const mutationObserver = new MutationObserver((mutationsList) => {
        this.data.items[0].text = mutationsList[0].target.innerHTML; // For check changing by inline tools
      });
      mutationObserver.observe(this._elements.input, { characterData: false, childList: true, attributes: false });
    };

    makeElements();
    setCypressAttribute();
    setMutationObserverForInput();
    this.setNumberingStatement();

    if (!this.readOnly) {
      // detect keydown on the last item to escape List
      this.onKeyDown = this.onKeyDown.bind(this);
      this._elements.container.addEventListener("keydown", this.onKeyDown, false);
    }

    return this._elements.container;
  }

  /**
   * @returns {ListData}
   * @public
   */
  save() {
    return this.data;
  }

  /**
   * Sanitizer rules
   *
   * @returns {object}
   */
  static get sanitize() {
    return {
      items: { br: true },
    };
  }

  getNextData(text) {
    const newNumberingLevels = cloneDeep(this.data.numberingLevels);
    switch (this.data.indentLevel % 3) {
      case this.DECIMAL_INDEX:
        // If numbering is 'number', add number and init others
        newNumberingLevels[this.data.indentPhase][this.DECIMAL_INDEX]++;
        newNumberingLevels[this.data.indentPhase][this.ALPHABET_NUMBER_INDEX] = 1;
        newNumberingLevels[this.data.indentPhase][this.ROMAN_NUMBER_INDEX] = 1;
        break;
      case this.ALPHABET_NUMBER_INDEX:
        // If numbering is 'alphabet', add alphabet number and init roman
        newNumberingLevels[this.data.indentPhase][this.ALPHABET_NUMBER_INDEX]++;
        newNumberingLevels[this.data.indentPhase][this.ROMAN_NUMBER_INDEX] = 1;
        break;
      case this.ROMAN_NUMBER_INDEX:
        // If numbering is 'roman', add roman number and init next phase's number as 1
        if (this.data.indentPhase < 2) newNumberingLevels[this.data.indentPhase + 1][this.DECIMAL_INDEX] = 1;
        newNumberingLevels[this.data.indentPhase][this.ROMAN_NUMBER_INDEX]++;
        break;
      default:
        break;
    }
    return {
      items: [{ text }],
      indentLevel: this.data.indentLevel, // 0 ~ 8
      indentPhase: this.data.indentPhase,
      numberingLevels: newNumberingLevels,
      number: this.data.number,
    };
  }

  addNewOrderedList(event) {
    /**
     * Prevent editor.js behavior
     */
    event.stopPropagation();

    /**
     * Prevent browser behavior
     */
    event.preventDefault();

    const value = this._elements.input.innerHTML;

    /** Prevent Default li generation if item is empty */
    if (value === "") {
      /** Insert New Paragraph Block and set caret */
      const currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
      this.api.blocks.delete();

      // If the block was the first block, which is index 0, it automatically creates new empty block.
      // So only create new block when the block index is not 0
      if (currentBlockIndex !== 0) {
        this.api.blocks.insert();
      }
      this.api.caret.setToBlock(currentBlockIndex);
    } else {
      /**
       * On other Enters, get content from caret till the end of the block
       * And move it to the new item
       */
      const endingFragment = Caret.extractFragmentFromCaretPositionTillTheEnd();
      const endingText = Dom.fragmentToString(endingFragment);
      /** Insert New list and set caret */
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      this.api.blocks.insert("orderedList", this.getNextData(endingText), undefined, blockIndex + 1, true);
      this.api.caret.setToBlock(blockIndex + 1);

      /** Remove ending text from current data */
      this.data.items[0].text = this._elements.input.innerHTML;
    }
  }

  /**
   * Handle backspace
   *
   * @param {KeyboardEvent} event
   */
  backspace(event) {
    const value = this._elements.input.innerHTML;
    const caretPos = getCaretPos(this._elements.input);
    const currentBlockIndex = this.api.blocks.getCurrentBlockIndex();

    /** When list is empty, convert to empty paragraph block */
    if (caretPos === 0 || value === "") {
      event.stopPropagation();
      this.api.blocks.delete(currentBlockIndex);
      this.api.blocks.insert("paragraph", { text: value }, {}, currentBlockIndex, true);
      this.api.caret.setToBlock(currentBlockIndex);
    }
  }

  setNumberingStatement() {
    this.data.indentPhase = Math.floor(this.data.indentLevel / 3);
    this.setNumberingRules();
    this.setNumberForMarkdownDownloading();
    this._elements.pseudoBefore.setAttribute("data-before", `${this.numbering[this.data.indentLevel % 3]}.`);
  }

  /**
   * Add indentation to current item
   *
   * @param {KeyboardEvent} event - keydown
   */
  addTab(event) {
    /**
     * Prevent editor.js behavior
     */
    event.stopPropagation();

    /**
     * Prevent browser tab behavior
     */
    event.preventDefault();
    this.data.indentLevel = this.data.indentLevel < this.MAX_INDENT_LEVEL ? this.data.indentLevel + 1 : this.MAX_INDENT_LEVEL;
    this.setNumberingStatement();
    this._elements.container.style.marginLeft = `${this.INDENT_MARGIN * this.data.indentLevel}px`;
  }

  shiftTab(event) {
    /**
     * Prevent editor.js behavior
     */
    event.stopPropagation();

    /**
     * Prevent browser tab behavior
     */
    event.preventDefault();

    this.data.indentLevel = this.data.indentLevel > 0 ? this.data.indentLevel - 1 : 0;
    this.setNumberingStatement();
    this._elements.container.style.marginLeft = `${this.INDENT_MARGIN * this.data.indentLevel}px`;
  }

  static convertToPlainHTML(data) {
    const listNumberTypes = ["1", "a", "i"];
    function makeList(listItems, nestedLevel) {
      return `<ol type=${listNumberTypes[nestedLevel % 3]}>${listItems.map((item) => (Array.isArray(item) ? makeList(item, ++nestedLevel) : `<li>${item}</li>`)).join("")}</ol >`;
    }
    const result = makeList(data, 0);
    return result;
  }

  // TODO: add paste handler
}

export default OrderedList;
